// FakeNewsCard.js
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
} from "@mui/material";
import {
  CheckCircleOutlined as CheckCircleOutlinedIcon,
  HighlightOffOutlined as HighlightOffOutlinedIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Launch as LaunchIcon,
  Download,
} from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const FakeNewsCard = ({
  item,
  index,
  isExpanded,
  currentImageIndex = 0,
  placeholder,
  onToggleExpansion,
  onOpenModal,
  truncateToWordLimit,
  onDownloadReport,
  formatDate,
}) => {
  return (
    <div key={item._id || index} className="fn-fact-card">
      {item.imageLink && item.imageLink.length > 0 && (
        <div className="fn-card-image-wrapper">
          <img
            src={item.imageLink[currentImageIndex] || placeholder}
            alt="Fact Check Evidence"
            className="fn-card-image"
            onError={(e) => {
              e.target.src = placeholder;
            }}
          />
          {item.imageLink.length > 1 && (
            <div className="fn-image-dots">
              {item.imageLink.map((_, dotIndex) => (
                <div
                  key={dotIndex}
                  className={`fn-dot ${
                    dotIndex === currentImageIndex ? "active" : ""
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="fn-card-content">
        <div className="fn-claim-text">
          {isExpanded ? item.claim : truncateToWordLimit(item.claim, 50)}
        </div>

        {item.claim && item.claim.split(" ").length > 50 && (
          <button
            onClick={() => onToggleExpansion(item._id)}
            className="fn-show-more-btn"
          >
            {isExpanded ? "Show Less" : "Show More"}
          </button>
        )}

        <div
          className={`fn-verdict-badge ${
            item.final_verdict ? "fn-verdict-true" : "fn-verdict-false"
          }`}
        >
          {item.final_verdict ? (
            <CheckCircleOutlinedIcon style={{ fontSize: "18px" }} />
          ) : (
            <HighlightOffOutlinedIcon style={{ fontSize: "18px" }} />
          )}
          <strong>{item.final_verdict ? "VERIFIED" : "FALSE"}</strong>
        </div>

        {item.fact && (
          <div className="fn-fact-box">
            <div className="fn-fact-header">
              <InfoIcon style={{ fontSize: "16px" }} />
              Fact Analysis
            </div>
            <div className="fn-fact-text">
              {isExpanded ? item.fact : truncateToWordLimit(item.fact, 60)}
            </div>
          </div>
        )}

        <div className="fn-meta-grid">
          <div>
            <strong>Date:</strong> {formatDate(item.dateTime)}
          </div>
          <div>
            <strong>ID:</strong> {item._id ? item._id.slice(-8) : "N/A"}
          </div>
        </div>

        {item.sources && item.sources.length > 0 && (
          <Accordion
            className="fn-sources-accordion"
            sx={{ borderRadius: "5px", marginBottom: "0px" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                variant="body2"
                sx={{ fontFamily: "'Host Grotesk', sans-serif" }}
              >
                Sources ({item.sources.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                {item.sources.slice(0, 3).map((source, sourceIndex) => (
                  <div key={sourceIndex} className="fn-source-item">
                    <a
                      href={source.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fn-source-link"
                    >
                      {source.title}
                      <LaunchIcon style={{ fontSize: "14px" }} />
                    </a>
                    <div className="fn-source-snippet">
                      {truncateToWordLimit(source.snippet, 25)}
                    </div>
                    {source.published_date && (
                      <div className="fn-source-date">
                        Published: {formatDate(source.published_date)}
                      </div>
                    )}
                  </div>
                ))}
                {item.sources.length > 3 && (
                  <Typography
                    variant="caption"
                    style={{
                      textAlign: "center",
                      color: "#666",
                      marginTop: "8px",
                      display: "block",
                    }}
                  >
                    +{item.sources.length - 3} more sources available
                  </Typography>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        )}

        {item.social_links && item.social_links.length > 0 && (
          <div className="fn-social-section">
            <div className="fn-social-title">Related Social Media</div>
            <div className="fn-social-chips">
              {item.social_links.slice(0, 3).map((social, socialIndex) => (
                <a
                  key={socialIndex}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fn-social-chip"
                >
                  {social.title
                    ? truncateToWordLimit(social.title, 8)
                    : "Social Post"}
                </a>
              ))}
              {item.social_links.length > 3 && (
                <span className="fn-social-chip">
                  +{item.social_links.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Buttons View Details & Download */}
        <div
          style={{
            paddingTop: "16px",
            textAlign: "right",
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button
            onClick={() => onOpenModal(item)}
            startIcon={<VisibilityOutlinedIcon />}
            variant="contained"
            sx={{
              fontFamily: '"Host Grotesk", sans-serif',
              background: "#e6f0ff", // very light blue
              color: "#004085", // dark blue text for contrast
              border: "none",
              borderRadius: "20px",
              padding: "8px 16px",
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(198, 217, 255, 0.6)", // lighter shadow
              textTransform: "none",
              minWidth: "auto",
              "&:hover": {
                background: "#c0d4ff", // slightly darker light blue on hover
                boxShadow: "0 4px 8px rgba(96, 134, 255, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 2px 4px rgba(102, 126, 234, 0.2)",
              },
            }}
          >
            View Details
          </Button>

          <Button
            onClick={() => onDownloadReport(item)}
            startIcon={<Download />}
            variant="contained"
            sx={{
              fontFamily: '"Host Grotesk", sans-serif',
              background: "#667eea",
              color: "white",
              border: "none",
              borderRadius: "20px",
              padding: "8px 16px",
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(102, 126, 234, 0.2)",
              textTransform: "none",
              minWidth: "auto",
              "&:hover": {
                background: "#5a52d5",
                boxShadow: "0 4px 8px rgba(102, 126, 234, 0.3)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 2px 4px rgba(102, 126, 234, 0.2)",
              },
            }}
          >
            Download
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FakeNewsCard;
